import { storableError } from '../../util/errors';
import { fetchDashboardMessages } from '../../util/api';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { transitions } from '../../transactions/transactionProcessPurchase';
import { SUCCESSFUL_TRANSITIONS } from '../InboxPage/InboxPage.duck';
import { CANCEL_TRANSITIONS } from './dashboardStats';

const PER_PAGE = 100;
const MAX_PAGES = 10;

export const SALES_LAST_TRANSITIONS = [
  transitions.CONFIRM_PAYMENT,
  transitions.DISPUTE,
  transitions.OPERATOR_DISPUTE,
  ...SUCCESSFUL_TRANSITIONS,
  ...CANCEL_TRANSITIONS,
];

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/DashboardPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/DashboardPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/DashboardPage/FETCH_LISTINGS_ERROR';
export const FETCH_SALES_REQUEST = 'app/DashboardPage/FETCH_SALES_REQUEST';
export const FETCH_SALES_SUCCESS = 'app/DashboardPage/FETCH_SALES_SUCCESS';
export const FETCH_SALES_ERROR = 'app/DashboardPage/FETCH_SALES_ERROR';
export const FETCH_MESSAGES_REQUEST = 'app/DashboardPage/FETCH_MESSAGES_REQUEST';
export const FETCH_MESSAGES_SUCCESS = 'app/DashboardPage/FETCH_MESSAGES_SUCCESS';
export const FETCH_MESSAGES_ERROR = 'app/DashboardPage/FETCH_MESSAGES_ERROR';
export const FETCH_OFFERS_SUCCESS = 'app/DashboardPage/FETCH_OFFERS_SUCCESS';

// ================ Reducer ================ //

const initialState = {
  listingRefs: [],
  listingsInProgress: false,
  listingsError: null,
  listingsFetched: false,
  saleRefs: [],
  salesInProgress: false,
  salesError: null,
  salesFetched: false,
  messages: [],
  messagesInProgress: false,
  messagesError: null,
  messagesFetched: false,
  // Offers live in transaction metadata; refs carry the user's role (isSale).
  offerRefs: [],
};

export default function dashboardPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return { ...state, listingsInProgress: true, listingsError: null };
    case FETCH_LISTINGS_SUCCESS:
      return { ...state, listingsInProgress: false, listingRefs: payload, listingsFetched: true };
    case FETCH_LISTINGS_ERROR:
      return { ...state, listingsInProgress: false, listingsError: payload, listingsFetched: true };
    case FETCH_SALES_REQUEST:
      return { ...state, salesInProgress: true, salesError: null };
    case FETCH_SALES_SUCCESS:
      return { ...state, salesInProgress: false, saleRefs: payload, salesFetched: true };
    case FETCH_SALES_ERROR:
      return { ...state, salesInProgress: false, salesError: payload, salesFetched: true };
    case FETCH_MESSAGES_REQUEST:
      return { ...state, messagesInProgress: true, messagesError: null };
    case FETCH_MESSAGES_SUCCESS:
      return { ...state, messagesInProgress: false, messages: payload, messagesFetched: true };
    case FETCH_MESSAGES_ERROR:
      return { ...state, messagesInProgress: false, messagesError: payload, messagesFetched: true };
    case FETCH_OFFERS_SUCCESS:
      return { ...state, offerRefs: payload };
    default:
      return state;
  }
}

// ================ Thunks ================ //

export const fetchAllPages = (query, params) => {
  const fetchPage = page => query({ ...params, page, perPage: PER_PAGE });
  return fetchPage(1).then(first => {
    const totalPages = Math.min(first?.data?.meta?.totalPages || 1, MAX_PAGES);
    const rest = [];
    for (let page = 2; page <= totalPages; page++) {
      rest.push(fetchPage(page));
    }
    return Promise.all(rest).then(others => [first, ...others]);
  });
};

const refsOf = responses =>
  responses.flatMap(r => r.data.data).map(({ id, type }) => ({ id, type }));

export const fetchDashboardListings = config => (dispatch, getState, sdk) => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;
  dispatch({ type: FETCH_LISTINGS_REQUEST });
  return fetchAllPages(params => sdk.ownListings.query(params), {
    // currentStock lets Your Listings hide sold-out cards (see isSoldOut).
    include: ['images', 'currentStock'],
    // 'default' is the uncropped variant; Your Listings shows whole card slabs.
    'fields.image': [
      'variants.default',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    // Matches ManageListingsPage.duck.js's queryOwnListings: exclude closed
    // and deleted listings from the dashboard.
    states: ['published', 'draft', 'pendingApproval'],
  })
    .then(responses => {
      responses.forEach(r => dispatch(addMarketplaceEntities(r)));
      dispatch({ type: FETCH_LISTINGS_SUCCESS, payload: refsOf(responses) });
    })
    .catch(e => dispatch({ type: FETCH_LISTINGS_ERROR, payload: storableError(e), error: true }));
};

export const fetchDashboardSales = () => (dispatch, getState, sdk) => {
  dispatch({ type: FETCH_SALES_REQUEST });
  return fetchAllPages(params => sdk.transactions.query(params), {
    only: 'sale',
    lastTransitions: SALES_LAST_TRANSITIONS,
    include: ['listing'],
    'fields.transaction': [
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'lineItems',
      'payinTotal',
    ],
    'fields.listing': ['title'],
  })
    .then(responses => {
      responses.forEach(r => dispatch(addMarketplaceEntities(r)));
      dispatch({ type: FETCH_SALES_SUCCESS, payload: refsOf(responses) });
    })
    .catch(e => dispatch({ type: FETCH_SALES_ERROR, payload: storableError(e), error: true }));
};

export const fetchRecentMessages = () => dispatch => {
  dispatch({ type: FETCH_MESSAGES_REQUEST });
  return fetchDashboardMessages()
    .then(response => dispatch({ type: FETCH_MESSAGES_SUCCESS, payload: response?.messages || [] }))
    .catch(e => dispatch({ type: FETCH_MESSAGES_ERROR, payload: storableError(e), error: true }));
};

const OFFERS_PER_ROLE = 50;

// Offers are inquiry transactions whose offer prices/times are stored in
// transaction metadata (see OfferButtons.js). Fetch the newest transactions in
// each role; buildActivity turns their metadata into offer items. Offers are
// an extra on Recent Activity, so a failure here just leaves them out.
export const fetchDashboardOffers = () => (dispatch, getState, sdk) => {
  const queryRole = only =>
    sdk.transactions
      .query({
        only,
        perPage: OFFERS_PER_ROLE,
        include: ['listing', 'customer', 'provider'],
        'fields.transaction': ['metadata', 'lastTransition', 'lastTransitionedAt'],
        'fields.listing': ['title'],
        'fields.user': ['profile.displayName'],
      })
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        return response.data.data.map(({ id, type }) => ({ id, type, isSale: only === 'sale' }));
      });
  return Promise.all([queryRole('sale'), queryRole('order')])
    .then(([sales, orders]) =>
      dispatch({ type: FETCH_OFFERS_SUCCESS, payload: [...sales, ...orders] })
    )
    .catch(() => dispatch({ type: FETCH_OFFERS_SUCCESS, payload: [] }));
};

// Sections load independently; each failure is stored, never thrown.
export const loadData = (params, search, config) => dispatch =>
  Promise.all([
    dispatch(fetchDashboardListings(config)),
    dispatch(fetchDashboardSales()),
    dispatch(fetchRecentMessages()),
    dispatch(fetchDashboardOffers()),
  ]);
